/**
 * Chronicle chains — meta-historical arcs that bind chronicles across
 * regions, generations, and causal lines. Each entry is an ordered
 * sequence of chronicle ids that the home-map chip (Layer 1, in
 * `RegionMarker.tsx`), the ledger filter (in `ArchivistLedger.tsx`),
 * cross-chronicle save reads (Layer 2, in `priorChronicleSave.ts`),
 * and historianNotes treat as a single arc.
 *
 * Scope. These are the meta-historical chains — cross-regional
 * connections (Napoleon's road from Paris to Madrid), conflicts
 * caused by an earlier conflict (Russia's collapse inside the First
 * World War), or sub-chronicles of a larger event (the Cold War
 * risings). Single-region chronologies (the British Isles' long road
 * from Rollo to Michael Collins; the Iberian peninsula's six
 * chronicles) are already visible from the home map's regional
 * layout — they don't get a chain entry.
 *
 * Placeholders. Chains include unbuilt chronicles too — the chip's
 * "(N of K)" reflects historical sequence regardless of build status,
 * and `unbuiltMembersOfChain()` returns the titles of any chain
 * members that are still `available: false` so the tooltip /
 * filter UI can render a "Coming: X, Y" line that updates
 * automatically when a placeholder ships.
 *
 * To extend: add a new chain to `CHRONICLE_CHAINS`. The home-map
 * chip and the ledger filter pick it up automatically. A chronicle
 * may belong to multiple chains (only `world-war-one` does today —
 * "The Great War & the Fall of the Tsars" + "The Two World Wars");
 * the chip / filter render iterates the membership array.
 */

import { chronicles as allChronicles } from "@/data/chronicles";

export type ChronicleChain = {
  /** Display name of the arc. Used as the chip label and the filter
   *  chip text. Keep under ~38 chars. */
  label: string;
  /** Through-line description shown on tooltip / filter hover.
   *  1-2 sentences; no spoilers (these are textbook history). */
  tooltip: string;
  /** Ordered chronicle ids — chronological by start year. May include
   *  placeholders (chronicles with `available: false`). */
  chronicleIds: string[];
};

export const CHRONICLE_CHAINS: ChronicleChain[] = [
  {
    label: "Behind the Iron Curtain",
    tooltip:
      "Four lives inside the Soviet bloc: a Leipzig lifetime under the Wall (1945–1990), the Budapest rising of 1956, the Prague Spring of 1968, and the year the Wall fell (1989). Each chronicle stands alone; playing them in order reveals cross-chronicle prose and reads prior protagonists' outcomes.",
    chronicleIds: [
      "two-germanies",
      "hungarian-revolution-1956",
      "prague-spring-1968",
      "fall-of-communism",
    ],
  },
  {
    label: "Two Hungarian Revolutions",
    tooltip:
      "Two Hungarian risings against foreign rule, a century apart: the 1848–49 war of independence, crushed by the Tsar's army at Világos, and the thirteen days of October 1956, crushed by Soviet tanks in Budapest. Both took up Petőfi and Kossuth; both ended under Russian arms.",
    chronicleIds: [
      "hungarian-revolution-1848",
      "hungarian-revolution-1956",
    ],
  },
  {
    label: "The Great War & the Fall of the Tsars",
    tooltip:
      "Russia fought the First World War from 1914; three years of Eastern Front bled the Tsarist state until the February Revolution fired in 1917. Brest-Litovsk pulled Russia out of the war in March 1918, but the civil war that followed ran another four years.",
    chronicleIds: [
      "world-war-one",
      "russian-revolution",
    ],
  },
  {
    label: "The Two World Wars",
    tooltip:
      "Two world wars on European soil within a single generation. Versailles in 1919 set the terms that 1939 came to undo; the second war's front lines largely tracked the unfinished business of the first.",
    chronicleIds: [
      "world-war-one",
      "world-war-two-europe",
    ],
  },
  {
    label: "The Napoleonic Era",
    tooltip:
      "From the grapeshot at Saint-Roch in 1795 to the abdication at Fontainebleau in 1814: the French Revolution's wars became Napoleon's wars, and the Peninsular campaign was the long Spanish theatre that bled him.",
    chronicleIds: [
      "french-revolution",
      "napoleonic-wars",
      "peninsular-war",
    ],
  },
  {
    label: "Reformation & the Wars of Religion",
    tooltip:
      "The breaking of Western Christendom and the wars it set loose: Luther's revolt from 1517, the Dutch revolt from 1566, the German wars of 1618–48, and the English wars of 1640–53. Calvinist exiles, Habsburg armies, and the same disputed theology fed every front.",
    chronicleIds: [
      "protestant-reformation",
      "eighty-years-war",
      "thirty-years-war",
      "english-civil-wars",
    ],
  },
  {
    label: "The Frontier Crusades",
    tooltip:
      "Three papally-authorized military campaigns on Christendom's frontiers in the same half-century: the Baltic pagans, the Languedoc heretics, the Andalusian Muslims. Different fronts, the same Crusader project.",
    chronicleIds: [
      "livonian-crusade",
      "albigensian-crusade",
      "reconquista-expansion",
    ],
  },
  {
    label: "The Viking Age",
    tooltip:
      "The Norse diaspora across two and a half centuries: the Dublin kings, the Iceland land-takers, the Christianisation of the Norwegian homeland, and Rollo's great-great-great-grandson conquering England in 1066.",
    chronicleIds: [
      "viking-dublin",
      "settlement-of-iceland",
      "christianization-scandinavia",
      "norman-conquest",
    ],
  },
  {
    label: "The Northern Wars",
    tooltip:
      "Two Swedish wars across the same Baltic and Polish-Lithuanian theatre, forty years apart. Charles X Gustav crossed the border in 1655; Charles XII crossed the same roads in 1700, and the same magnate families were caught in both.",
    chronicleIds: [
      "the-deluge",
      "great-northern-war",
    ],
  },
  {
    label: "The Medici and the Wars of Italy",
    tooltip:
      "A self-made banker's rise in the House of Medici (Florence, 1434–1498), and the son who takes up the sword as the Italian Wars (1494–1559) grind the free Italian states between France, Spain, and the Empire. Carry the banker's family to Bologna, and his son's story opens.",
    chronicleIds: [
      "medici-florence",
      "italian-wars",
    ],
  },
  {
    label: "The Ottoman Wars in Europe",
    tooltip:
      "Three and a half centuries of Ottoman war in Europe: the fall of Constantinople in 1453, the sultans' march on Vienna by 1683, and the long unraveling that the Serbian and Greek risings began in the early 1800s.",
    chronicleIds: [
      "siege-of-constantinople",
      "ottoman-expansion-europe",
      "serbian-uprisings",
      "greek-independence",
    ],
  },
];

/** Returns every chronicle chain + position (1-indexed) the given
 *  chronicle belongs to. Most chronicles return a single-element array
 *  or empty. `world-war-one` returns two (Great War & Tsars + Two
 *  World Wars). The home-map chip and the ledger's "X members"
 *  preview iterate the result. */
export function getChronicleChains(chronicleId: string): Array<{ chain: ChronicleChain; position: number }> {
  const out: Array<{ chain: ChronicleChain; position: number }> = [];
  for (const chain of CHRONICLE_CHAINS) {
    const idx = chain.chronicleIds.indexOf(chronicleId);
    if (idx >= 0) out.push({ chain, position: idx + 1 });
  }
  return out;
}

/** Returns the titles of chain members that are still placeholders
 *  (chronicles marked `available: false` in `@/data/chronicles`).
 *  Used by tooltips and the ledger's filter to render a
 *  "Coming: X, Y" line so the player knows which members of the arc
 *  haven't shipped yet. Updates automatically when a placeholder is
 *  flipped to `available: true` — no chain-data edit needed. */
export function unbuiltMembersOfChain(chain: ChronicleChain): string[] {
  const titles: string[] = [];
  for (const id of chain.chronicleIds) {
    const c = allChronicles.find((x) => x.id === id);
    if (c && !c.available) titles.push(c.title);
  }
  return titles;
}
